import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { supabase } from "@/integrations/supabase/client"
import type { User } from "@supabase/supabase-js"

interface AuthContextType {
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
  updateProfile: (firstName: string, lastName: string) => Promise<{ error: Error | null }>
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>
  deleteAccount: () => Promise<{ error: Error | null }>
  uploadAvatar: (file: File) => Promise<{ url: string | null; error: Error | null }>
  deleteAvatar: () => Promise<{ error: Error | null }>
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
  updateProfile: async () => ({ error: null }),
  updatePassword: async () => ({ error: null }),
  deleteAccount: async () => ({ error: null }),
  uploadAvatar: async () => ({ url: null, error: null }),
  deleteAvatar: async () => ({ error: null }),
})

const DEV = import.meta.env.DEV
const log = (...args: unknown[]) => { if (DEV) console.log(...args) }

/** Cheap signature for detecting whether a user payload has *really* changed.
 * Supabase fires TOKEN_REFRESHED hourly with a fresh `session.user` object —
 * without this short-circuit, every consumer in the app re-renders every
 * hour even though the relevant identity bits never changed. */
function userSignature(u: User | null): string {
  if (!u) return ""
  const m = u.user_metadata ?? {}
  return [
    u.id,
    u.email ?? "",
    m.first_name ?? "",
    m.last_name ?? "",
    m.avatar_url ?? "",
  ].join("|")
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  // Track the last user signature so we can skip setState on TOKEN_REFRESHED
  // events that don't actually change identity / metadata.
  const lastSigRef = useRef<string>("")
  const userRef = useRef<User | null>(null)
  userRef.current = user

  // Stable setter that no-ops when the user identity hasn't materially changed.
  const setUserIfChanged = useCallback((next: User | null) => {
    const sig = userSignature(next)
    if (sig === lastSigRef.current) return
    lastSigRef.current = sig
    setUser(next)
  }, [])

  useEffect(() => {
    log('🔐 [AUTH] Initializing auth context')

    // Get initial session
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser()

        if (error) {
          const isMissingSession = /auth session missing/i.test(error.message)
          if (!isMissingSession) {
            console.error('❌ [AUTH] Error fetching user:', error.message)
          }
          if (error.message.includes('Refresh Token') || error.message.includes('Invalid')) {
            await supabase.auth.signOut({ scope: 'local' })
          }
        }

        setUserIfChanged(user)
        setLoading(false)
      } catch (err) {
        console.error('❌ [AUTH] Exception during user fetch:', err)
        try {
          await supabase.auth.signOut({ scope: 'local' })
        } catch (signOutError) {
          console.warn('⚠️ [AUTH] Failed to clear session:', signOutError)
        }
        setUserIfChanged(null)
        setLoading(false)
      }
    }

    getUser()

    log('🔐 [AUTH] Setting up auth state listener')
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      log('🔐 [AUTH] Auth state changed', { event, hasUser: !!session?.user })
      setUserIfChanged(session?.user ?? null)
      setLoading(false)
    })

    return () => {
      log('🔐 [AUTH] Cleaning up auth listener')
      subscription.unsubscribe()
    }
  }, [setUserIfChanged])

  const signOut = useCallback(async () => {
    log('🚪 [AUTH] Sign out initiated')
    try {
      const { error } = await supabase.auth.signOut()
      if (error) {
        console.error('❌ [AUTH] Sign out error:', error.message)
        throw error
      }
      setUserIfChanged(null)
    } catch (err) {
      console.error('❌ [AUTH] Exception during sign out:', err)
      setUserIfChanged(null)
      throw err
    }
  }, [setUserIfChanged])

  const updateProfile = useCallback(async (firstName: string, lastName: string) => {
    try {
      const { data, error } = await supabase.auth.updateUser({
        data: { first_name: firstName, last_name: lastName },
      })

      if (error) throw error

      const current = userRef.current
      if (current) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ first_name: firstName, last_name: lastName } as any)
          .eq('user_id', current.id)

        if (profileError) {
          console.error('Failed to update profile table:', profileError)
        }
      }

      if (data.user) setUserIfChanged(data.user)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [setUserIfChanged])

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword })
      if (error) throw error
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [])

  const deleteAccount = useCallback(async () => {
    try {
      const current = userRef.current
      if (!current?.id) return { error: new Error('Not signed in') }
      const { error: deleteExpensesError } = await supabase
        .from('user_expenses')
        .delete()
        .eq('user_id', current?.id)

      if (deleteExpensesError) throw deleteExpensesError

      const { error: deleteIncomesError } = await supabase
        .from('user_incomes')
        .delete()
        .eq('user_id', current?.id)

      if (deleteIncomesError) throw deleteIncomesError

      // Best-effort cleanup of the rest of this user's data so deleting an
      // account doesn't leave orphaned rows behind (budgets, sub-accounts,
      // goals, cards, categories, notifications, streak, recurring, profile).
      // The financial records above are the privacy-critical ones and are
      // already gone, so we don't abort the whole deletion if one of these
      // fails (empty table, RLS quirk). Ordered so rows that reference a
      // sub-account are removed before user_sub_accounts itself.
      const SUPPLEMENTARY_TABLES = [
        'budgets',
        'recurring_transactions',
        'user_payment_cards',
        'user_categories',
        'savings_goals',
        'user_notifications',
        'streak_state',
        'user_sub_accounts',
        'profiles',
      ] as const

      for (const table of SUPPLEMENTARY_TABLES) {
        const { error } = await supabase.from(table).delete().eq('user_id', current.id)
        if (error) {
          console.warn(`[deleteAccount] could not clear ${table}:`, error.message)
        }
      }

      await supabase.auth.signOut()
      setUserIfChanged(null)
      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [setUserIfChanged])

  const uploadAvatar = useCallback(async (file: File) => {
    try {
      const current = userRef.current
      if (!current) throw new Error('No user logged in')

      const { validateImageFile } = await import('@/lib/utils/file-validation')
      const validation = await validateImageFile(file)

      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid file')
      }

      const currentAvatarUrl = current.user_metadata?.avatar_url
      if (currentAvatarUrl) {
        const oldPath = currentAvatarUrl.split('/').slice(-2).join('/')
        await supabase.storage.from('avatars').remove([oldPath])
      }

      const fileExt = file.name.split('.').pop()
      const filePath = `${current.id}/avatar.${fileExt}`

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true })

      if (uploadError) throw uploadError

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      const avatarUrl = `${publicUrl}?t=${Date.now()}`

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: avatarUrl } as any)
        .eq('user_id', current.id)

      if (updateError) throw updateError

      const { data, error: authError } = await supabase.auth.updateUser({
        data: { avatar_url: avatarUrl },
      })

      if (authError) throw authError
      if (data.user) setUserIfChanged(data.user)

      return { url: avatarUrl, error: null }
    } catch (error) {
      return { url: null, error: error as Error }
    }
  }, [setUserIfChanged])

  const deleteAvatar = useCallback(async () => {
    try {
      const current = userRef.current
      if (!current) throw new Error('No user logged in')

      const currentAvatarUrl = current.user_metadata?.avatar_url
      if (currentAvatarUrl) {
        const filePath = currentAvatarUrl.split('/').slice(-2).join('/')
        await supabase.storage.from('avatars').remove([filePath])
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: null } as any)
        .eq('user_id', current.id)

      if (updateError) throw updateError

      const { data, error: authError } = await supabase.auth.updateUser({
        data: { avatar_url: null },
      })

      if (authError) throw authError
      if (data.user) setUserIfChanged(data.user)

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }, [setUserIfChanged])

  const value = useMemo<AuthContextType>(() => ({
    user,
    loading,
    signOut,
    updateProfile,
    updatePassword,
    deleteAccount,
    uploadAvatar,
    deleteAvatar,
  }), [user, loading, signOut, updateProfile, updatePassword, deleteAccount, uploadAvatar, deleteAvatar])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}
